/* eslint-disable no-case-declarations */
import { Component, Input, OnInit } from "@angular/core";
import { Observable, Subject, takeUntil } from "rxjs";
import { Store } from "@ngxs/store";
// Modules
import {
	BroadcastService,
	ChangeEventStatusChannelAction,
	EVENT_STATUS,
	GlobalUtils,
	ORDER_EVENT_TYPE,
	PermissionService,
	SignalRState,
	ToasterType,
	UserContextModel
} from "@cv-invision/core";
import { PERMISSION } from "@cv-invision/cv.invision-views.ux";
// Models
import { OrderDetailModel } from "../../../state/model/order-detail.model";
// Actions
import { SetOrderLocked, SetOrderUnlocked } from "../../../state/actions/quality-order-detail.actions";
// Services
import { QualityDocumentService } from "../../../service/document-quality.service";
import { OverviewSubHeaderService } from "../service/overview-sub-header.service";

@Component({
	selector: 'cv-ci-sub-header-lock-button',
	templateUrl: './lock-button.component.html',
	styleUrls: ['./lock-button.component.scss']
})
export class LockButtonComponent implements OnInit {
	@Input() orderInboxModel: OrderDetailModel;
	channels$: Observable<any> = this.store.select(SignalRState.channelEvents);

	unlockingOrder = false;
	showSaveButton = false;
	savingOrderChanges = false;
	isOrderDetailVisible: boolean;
	userContext: UserContextModel;

	public tooltipPlacement = 'top';

	private atComponentDestroy = new Subject();

	get lockedByName(): string {
		let response = '';
		if (this.orderInboxModel.lockedBy) {
			const { lockedBy } = this.orderInboxModel;
			if (lockedBy.firstName === null && lockedBy.lastName === null) {
				response = 'System';
			} else {
				response = `${lockedBy.firstName} ${lockedBy.lastName || ''}`;
			}
		}

		return response;
	}

	get canShowEditUnlock(): boolean {
		return this.userPermision.hasPermission(PERMISSION.INTELLIGENCE_EDIT_ORDER);
	}

	get canForceUnlock(): boolean {
		return this.userPermision.hasPermission(PERMISSION.INTELLIGENCE_FORCEUNLOCK);
	}

	constructor(
		private store: Store,
		private broadcastService: BroadcastService,
		private qualityService: QualityDocumentService,
		private headerService: OverviewSubHeaderService,
		private userPermision: PermissionService
	) {
		this.tooltipPlacement = GlobalUtils.getTooltipPlacement();
	}

	ngOnInit(): void {
		this.userContext = this.store.selectSnapshot(state => state.user);

		this.channels$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((channels: any) => {
				if (channels && channels[`orders:${this.orderInboxModel.id}`]) {
					channels[`orders:${this.orderInboxModel.id}`].events.forEach((channelEvent: any) => {
						let clearEvent = false;
						if (channelEvent.status === 'new') {
							switch (channelEvent.event) {
								// On order locked.
								case ORDER_EVENT_TYPE.ORDER_LOCKED:
									this.broadcastService.broadcast({
										name: ToasterType.SUCCESS,
										event: {
											message: this.headerService.getOrderMsg(channelEvent.data.user)
										}
									});
									const userData = {
										id: channelEvent.data.user.id,
										firstName: channelEvent.data.user.firstName,
										lastName: channelEvent.data.user.lastName
									};

									this.store.dispatch(new SetOrderLocked(new Date(), userData));
									clearEvent = true;
									break;
								// On order unlocked.
								case ORDER_EVENT_TYPE.ORDER_UNLOCKED:
									this.broadcastService.broadcast({
										name: ToasterType.SUCCESS,
										event: {
											message: this.headerService.getOrderMsg(channelEvent.data.user, false)
										}
									});
									this.store.dispatch(new SetOrderUnlocked());
									clearEvent = true;
									break;
							}
							// Change event to old
							if (clearEvent) {
								const orderChannel = `orders:${this.orderInboxModel.id}`;
								this.store.dispatch(new ChangeEventStatusChannelAction(orderChannel, EVENT_STATUS.OLD));
							}
						}
					});
				}
		});
	}

	/**
	 * On button edit order click, lock the order.
	 */
	lockOrder(): void {
		this.qualityService.lockOrder(this.orderInboxModel.id).subscribe(
			result => {
				if (result.succeed) {
					this.broadcastService.broadcast({
						name: ToasterType.SUCCESS,
						event: {
							message: 'Order locked: Now only you can edit this order'
						}
					});
					this.store.dispatch(
						new SetOrderLocked(
							new Date(),
							{
								id: this.userContext.id,
								firstName: this.userContext.firstName,
								lastName: this.userContext.lastName
							}
						)
					);
				} else if (result.message && result.message.length) {
					this.broadcastService.broadcast({
						name: ToasterType.ERROR,
						event: {
							error: result.message
						}
					});
				}
			},
			error => console.error(error)
		);
	}

	/**
	 * On button unlock order click.
	 * 1 - Save Order Document data
	 * 2 - Save Photos as primary.
	 * 3 - Unlock Order.
	 */
	onUnLockOrder(isForceUnlock: boolean = false): void {
		this.unlockingOrder = true;
		this.unlockOrder(isForceUnlock);
	}

	/**
	 * Call api to unlock order
	 */
	private unlockOrder(isForceUnlock: boolean = false): void {
		this.qualityService.unlockOrder(this.orderInboxModel.id, isForceUnlock).subscribe(
			(result: { succeed: any }) => {
				if (result.succeed) {
					this.broadcastService.broadcast({
						name: ToasterType.SUCCESS,
						event: {
							message: 'Order unlocked: Now anyone can edit this order'
						}
					});
					this.store.dispatch(new SetOrderUnlocked());
					this.unlockingOrder = false;
				} else {
					this.unlockingOrder = false;
				}
			},
			(error: any) => {
				console.error(error);
				this.unlockingOrder = false;
			}
		);
	}
}
